import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import cn from "classnames"
import React, { useRef, useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import { harvestExperimentEvent } from "@/common/harvester"
import { locale } from "@/common/locales"
import { isBlank } from "@/common/util"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const regularHeroHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [2],
      HTMLAttributes: {
        class: "mb-3",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const regularHeroParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-4 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const RegularHeroFragment = graphql(`
  fragment FrontpagesRegularHero on FrontpagesRegularHero {
    id
    primaryButtonLabel
    primaryButtonUrl
    secondaryButtonLabel
    secondaryButtonUrl
    headingJSON: heading
    description

    desktopImage: desktop {
      data
      width
      height
      pngDesktop
      pngDesktop2x
      pngTablet
      pngTablet2x
      webpDesktop
      webpDesktop2x
      webpTablet
      webpTablet2x
    }

    mobileImage: mobile {
      data
      width
      height
      pngMobile
      pngMobile2x
      webpMobile
      webpMobile2x
    }
  }
`)

const RegularHeroBlock: React.FC<BlockProps<typeof RegularHeroFragment>> = ({ blockable, businessRole, heroV2 }) => {
  const block = getFragment(RegularHeroFragment, blockable)
  const primaryButton = !isBlank(block.primaryButtonLabel) && !isBlank(block.primaryButtonUrl)
  const secondaryButton = !isBlank(block.secondaryButtonLabel) && !isBlank(block.secondaryButtonUrl)
  const twoButtons = primaryButton && secondaryButton
  const contentRef = useRef<HTMLDivElement>(null)
  const [overlayHeight, setOverlayHeight] = useState(contentRef.current?.clientHeight)
  const { t } = useTranslation()
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    setOverlayHeight(contentRef.current?.clientHeight)
  }, [setOverlayHeight])

  const secondaryButtonLabel = heroV2 ? "Offertförfrågan" : block.secondaryButtonLabel
  const secondaryButtonUrl = heroV2 && businessRole ? t("route.quote") : block.secondaryButtonUrl
  const experiment = businessRole ? "business-hero-secondary-button-9217" : "consumer-hero-secondary-button-9217"

  const handleSecondaryClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      harvestExperimentEvent(experiment, heroV2 ? "1" : "0", "button-click")

      if (secondaryButtonUrl) {
        window.location.href = secondaryButtonUrl
      }
    },
    [experiment, heroV2, secondaryButtonUrl]
  )

  return (
    <div>
      <div className="container pt-0">
        <div className="relative h-[436px] overflow-hidden rounded-2xl lg:h-[376px]">
          <div className="absolute inset-0 z-0">
            <picture>
              <source
                srcSet={`${block.desktopImage.webpDesktop}, ${block.desktopImage.webpDesktop2x} 2x`}
                media="(min-width: 1280px)"
                type="image/webp"
              />
              <source
                srcSet={`${block.desktopImage.pngDesktop}, ${block.desktopImage.pngDesktop2x} 2x`}
                media="(min-width: 1280px)"
              />
              <source
                srcSet={`${block.desktopImage.webpTablet}, ${block.desktopImage.webpTablet2x} 2x`}
                media="(min-width: 640px)"
                type="image/webp"
              />
              <source
                srcSet={`${block.desktopImage.pngTablet}, ${block.desktopImage.pngTablet2x} 2x`}
                media="(min-width: 640px)"
              />
              <source
                srcSet={`${block.mobileImage.webpMobile}, ${block.mobileImage.webpMobile2x} 2x`}
                type="image/webp"
              />
              <img
                srcSet={`${block.mobileImage.pngMobile}, ${block.mobileImage.pngMobile2x} 2x`}
                width={block.mobileImage.width}
                height={block.mobileImage.height}
                className="h-full w-full object-cover object-bottom sm:object-left"
                loading="lazy"
              />
            </picture>
          </div>
          <div
            className="absolute inset-y-0 hidden w-[550px] sm:block"
            style={{
              maskImage:
                "linear-gradient(90deg, rgba(255, 255, 255, 0.80) 0.36%, rgba(255, 255, 255, 0.60) 85.19%, rgba(255, 255, 255, 0.00) 99.67%)",
            }}
          ></div>
          <div
            className="absolute inset-y-0 w-full sm:hidden"
            style={{
              height: overlayHeight,
              maskImage:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.80) 0.36%, rgba(255, 255, 255, 0.60) 85.19%, rgba(255, 255, 255, 0.00) 99.67%)",
            }}
          ></div>

          <div
            className="relative z-0 flex items-start px-4 pb-16 pt-6 sm:h-full sm:w-[550px] sm:pb-0 sm:pl-16 sm:pr-4 sm:pt-[74px]"
            ref={contentRef}
          >
            <div>
              <StaticTiptapDisplay extensions={regularHeroHeadingStack} json={block.headingJSON} />
              <StaticTiptapDisplay extensions={regularHeroParagraphStack} json={block.description} />

              <div className="flex flex-wrap gap-x-3 gap-y-4">
                {primaryButton && (
                  <a
                    href={block.primaryButtonUrl || ""}
                    className={cn("button button-emphasis button-large m-0 max-w-full whitespace-normal", {
                      "w-full sm:w-auto": twoButtons,
                    })}
                    data-gtm="fp-primary-hero-button"
                  >
                    {block.primaryButtonLabel}
                  </a>
                )}

                {secondaryButton && (
                  <a
                    href={secondaryButtonUrl || ""}
                    className={cn("button button-secondary button-large !m-0 max-w-full whitespace-normal", {
                      "w-full sm:w-auto": twoButtons,
                      hydrated: hydrated,
                    })}
                    data-gtm="fp-secondary-hero-button"
                    onClick={locale == "sv-SE" ? handleSecondaryClick : undefined}
                  >
                    {secondaryButtonLabel}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegularHeroBlock
